import { ApiResponse, ApiRequestConfig, ApiConfig } from './types';

export class Api {
  private baseUrl: string;
  private defaultConfig: ApiRequestConfig;
  private defaultHeaders: Record<string, string>;

  constructor(config: ApiConfig) {
    this.baseUrl = config.baseUrl.replace(/\/$/, '');
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      ...config.headers,
    };

    this.defaultConfig = {
      method: 'GET',
      timeout: config.timeout || 10000,
      retries: config.retries || 3,
      headers: this.defaultHeaders,
    };
  }

  private async makeRequest<T>(
    endpoint: string,
    params?: any,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<T>> {
    const finalConfig = { ...this.defaultConfig, ...config };
    const url = `${this.baseUrl}${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`;

    let attempt = 0;
    const maxRetries = finalConfig.retries || 3;

    while (attempt <= maxRetries) {
      try {
        const controller = new AbortController();
        const timeoutId = finalConfig.timeout && finalConfig.timeout > 0
          ? setTimeout(() => controller.abort(), finalConfig.timeout)
          : null;

        const requestOptions: RequestInit = {
          method: finalConfig.method,
          headers: finalConfig.headers,
          signal: controller.signal,
        };

        if (params && ['POST', 'PUT', 'PATCH'].includes(finalConfig.method || 'GET')) {
          if (params instanceof FormData) {
            requestOptions.body = params;
            // Remove Content-Type header to let browser set it with boundary
            const headers = { ...finalConfig.headers };
            delete headers['Content-Type'];
            requestOptions.headers = headers;
          } else {
            requestOptions.body = JSON.stringify(params);
          }
        }

        if (params && finalConfig.method === 'GET') {
          const searchParams = new URLSearchParams(params);
          const separator = url.includes('?') ? '&' : '?';
          const finalUrl = `${url}${separator}${searchParams.toString()}`;

          const response = await fetch(finalUrl, requestOptions);
          if (timeoutId) clearTimeout(timeoutId);

          return await this.handleResponse<T>(response);
        }

        const response = await fetch(url, requestOptions);
        if (timeoutId) clearTimeout(timeoutId);

        return await this.handleResponse<T>(response);

      } catch (error) {
        attempt++;

        if (attempt > maxRetries) {
          return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error occurred',
          };
        }

        await this.delay(Math.pow(2, attempt) * 1000);
      }
    }

    return {
      success: false,
      error: 'Max retries exceeded',
    };
  }

  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    try {
      const contentType = response.headers.get('content-type');
      const isJson = contentType?.includes('application/json');

      if (!response.ok) {
        const errorData = isJson ? await response.json() : await response.text();
        return {
          success: false,
          error: typeof errorData === 'string'
            ? errorData
            : (typeof errorData === 'object' && errorData !== null && 'detail' in errorData && typeof (errorData as any).detail === 'string'
              ? (errorData as any).detail
              : 'Request failed'),
        };
      }

      const data = isJson ? await response.json() : await response.text();

      return {
        success: true,
        data: data as T,
      };
    } catch (error) {
      return {
        success: false,
        error: 'Failed to parse response',
      };
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async get<T>(endpoint: string, params?: any, config?: ApiRequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, params, { ...config, method: 'GET' });
  }

  async post<T>(endpoint: string, params?: any, config?: ApiRequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, params, { ...config, method: 'POST' });
  }

  async put<T>(endpoint: string, params?: any, config?: ApiRequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, params, { ...config, method: 'PUT' });
  }

  async delete<T>(endpoint: string, params?: any, config?: ApiRequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, params, { ...config, method: 'DELETE' });
  }

  async patch<T>(endpoint: string, params?: any, config?: ApiRequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, params, { ...config, method: 'PATCH' });
  }

  setHeader(key: string, value: string): void {
    this.defaultHeaders[key] = value;
    this.defaultConfig.headers = this.defaultHeaders;
  }

  removeHeader(key: string): void {
    delete this.defaultHeaders[key];
    this.defaultConfig.headers = this.defaultHeaders;
  }
}

export const createApi = (config: ApiConfig): Api => {
  return new Api(config);
};